import React, { useCallback } from 'react';
import { Dialog, DialogActions, DialogContent, DialogProps, Grid, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { RootState } from 'store/rootReducer';
import RenderComponent from 'components/common/RenderComponent';
import { EventHostsSelectors } from 'store/selectors';
import { makeStyles } from 'tss-react/mui';
import Button from '../common/Button/Button';
import Avatar from '../common/Avatar';
import CustomDialogTitle from '../common/CustomDialogTitle';
import { routes } from '../../utils/routing';

interface IProps extends DialogProps {
  hostId: number;
  eventId: number;
}

const useStyles = makeStyles()((theme) => ({
  item: {
    padding: theme.spacing(3),
    borderRadius: 8,
    background: theme.palette.background.default,
  },
  avatar: {
    width: 99,
    height: 99,
  },
  bio: {
    position: 'relative',
    display: '-webkit-box',
    WebkitLineClamp: '3',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: ' [..]',
    whiteSpace: 'normal',
  },
}));

const EventHostModal: React.FC<IProps> = (props) => {
  const { hostId, eventId, onClose, ...rest } = props;
  const router = useRouter();
  const { classes } = useStyles();
  const hostState = useSelector((state: RootState) => EventHostsSelectors.eventHostById(state, hostId));
  const route = routes.specialHost(hostState.organization_host.id, {}, { event_id: eventId });

  const handleOnClose = useCallback(() => {
    onClose({}, null);
  }, [onClose]);

  const handleViewHostEvents = () => {
    router.push(route.href);
    handleOnClose();
  };

  return (
    <Dialog id={(Math.random() + 1).toString(36).substring(7)} maxWidth="sm" onClose={onClose} {...rest}>
      <RenderComponent
        isLoading={hostId && !hostState}
        renderContent={() => (
          <>
            <CustomDialogTitle
              closeButtonColor="inherit"
              textTitleColor="inherit"
              padding="16px 16px"
              onClose={handleOnClose}
            />
            <DialogContent style={{ marginTop: -36, padding: '0px 22px 20px 22px' }}>
              <Grid container spacing={1.75}>
                <Grid item className={classes.avatar}>
                  <Avatar fullWidth src={hostState.organization_host?.image?.original_url} />
                </Grid>
                <Grid item xs style={{ minWidth: 470, minHeight: 400 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} style={{ paddingTop: 27 }}>
                      <Typography variant="h5">{hostState.organization_host.name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" fontWeight={500}>
                        {hostState.host_roles[0]?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: hostState.bio,
                        }}
                        variant="body1"
                        fontWeight={400}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: '24px 40px 24px 40px' }}>
              <Grid container justifyContent="end" spacing={1}>
                <Grid item>
                  <Button
                    onClick={handleOnClose}
                    style={{ height: 40, minWidth: 107 }}
                    rounded
                    variant="outlined"
                    color="primary"
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    onClick={handleViewHostEvents}
                    style={{ height: 40, minWidth: 107 }}
                    rounded
                    variant="contained"
                    color="primary"
                  >
                    View All Events
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  );
};

export default React.memo(EventHostModal);
